import { cloneDeep, isEqual } from 'lodash-es';
import { useEffect, useMemo, useRef, useState } from 'react';
import { FieldErrors, FormProvider, useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { twMerge } from 'tailwind-merge';

import { zodResolver } from '@hookform/resolvers/zod';
import { SerializedError } from '@reduxjs/toolkit';
import { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import type { OnCloseType } from '@semcore/ui/side-panel';

import {
  contentApi,
  useCreateSliderContentMutation,
  useGetSliderContentQuery,
  usePublishContentMutation,
  useUnpublishContentMutation,
  useUpdateSliderContentMutation,
} from '@/entities/content/api/contentApi';
import type { Content, ContentStyles } from '@/entities/content/api/types';
import { publisherRoutes, useAppDispatch } from '@/shared/lib';
import { stopSubmition } from '@/shared/lib/utils/stopPropagation';
import Button from '@/shared/ui/button';
import PageLoader from '@/shared/ui/PageLoader';
import {
  AnimationStyleSettings,
  ColorsSettings,
  Controls,
  LinkButtonSettings,
  LogoSettings,
  TextSettings,
  TextStyleAndPositionSettings,
} from '@/widgets/content-styles';

import { sliderContentDefaults } from '../../features/content-form/lib/defaults';
import {
  SliderContentIn,
  SliderContentPublicationSchema,
  SliderContentSchema,
} from '../../features/content-form/lib/schema';
import PresetSelector from '../../features/content-form/ui/PresetSelector';
import Sidebar from '../../features/content-form/ui/Sidebar';
import {
  MetadataSettings,
  SlidesList,
} from '../../features/content-form/ui/Slides';

import { SlidesPlayerWrapper } from './SlidesPlayerWrapper';

const AUTOSAVE_INTERVAL = 10_000;

const getErrorMessage = (
  error: FetchBaseQueryError | SerializedError | undefined
): string => {
  if (!error) return 'Something went wrong. Please try again.';

  if ('data' in error) {
    const data = error.data as { message?: string } | undefined;
    if (data?.message) return data.message;
  }

  if ('message' in error && error.message) {
    return error.message;
  }

  return 'Something went wrong. Please try again.';
};

type Props = {
  onClose: OnCloseType;
};

export function SliderContentForm(props: Props) {
  const params = useParams();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const form = useForm({
    resolver: zodResolver(SliderContentSchema),
    defaultValues: sliderContentDefaults,
  });

  const mode = params.id === 'new' ? 'create' : 'update';

  const contentQuery = useGetSliderContentQuery(params.id!, {
    skip: mode === 'create',
  });

  useEffect(() => {
    if (mode === 'update' && contentQuery.isSuccess) {
      form.reset(contentQuery.data, {
        // TODO: fix bug when we create content
        // keepValues: true,
      });
    }
  }, [mode, contentQuery.isSuccess]);

  useEffect(() => {
    if (contentQuery.isError) {
      console.error("Can't load content with id:", params.id);
      console.error({ error: contentQuery.error });
      navigate(publisherRoutes.content);
    }
  }, [contentQuery.isError]);

  const [createContent, createContentMutation] =
    useCreateSliderContentMutation();
  const [updateContent, updateContentMutation] =
    useUpdateSliderContentMutation();
  const [publishContent, publishContentMutation] = usePublishContentMutation();
  const [unpublishContent, unpublishContentMutation] =
    useUnpublishContentMutation();

  // eslint-disable-next-line react-hooks/exhaustive-deps
  async function handleSave(andPublish: boolean = false) {
    if (createContentMutation.isLoading || updateContentMutation.isLoading) {
      console.log('already saving');
      return;
    }

    if (mode === 'create') {
      console.log('create draft');

      await form.handleSubmit(
        async (formValues) => {
          const createdContent = await createContent({
            ...formValues,
            status: 0,
          }).unwrap();

          const content = createdContent.data;
          await dispatch(
            contentApi.util.upsertQueryData(
              'getSliderContent',
              `${content.id}`,
              content
            )
          );

          navigate(`/publisher/content/slider/${content.id}`);
          form.setValue('id', content.id);
        },
        (formErrors) => {
          console.log({ formErrors });
        }
      )();
    } else if (mode === 'update') {
      if (form.getValues('status') === 1) {
        // @ts-expect-error
        // eslint-disable-next-line
        form.control._options.resolver = zodResolver(
          SliderContentPublicationSchema
        );

        await form.handleSubmit(
          async (formValues) => {
            await updateContent({ ...formValues, id: params.id }).unwrap();
          },
          (formErrors) => {
            console.log({ formErrors });
          }
        )();
      } else {
        await form.handleSubmit(
          async (formValues) => {
            console.log('save draft');
            await updateContent({ ...formValues, id: params.id }).unwrap();
          },
          (formErrors) => {
            console.log({ formErrors });
          }
        )();
      }
    }

    if (andPublish) {
      console.log('now we need to publish this');
      // @ts-expect-error
      // eslint-disable-next-line
      form.control._options.resolver = zodResolver(
        SliderContentPublicationSchema
      );

      form.handleSubmit(
        async () => {
          console.log('Publish', form.getValues('id'));
          if (form.getValues('id')) {
            await publishContent(form.getValues('id')).unwrap();
            form.setValue('status', 1);
          }
        },
        (formErrors) => {
          console.log("Can't publish", form.getValues('id'), { formErrors });
        }
      )();
    }
  }

  // AUTOSAVE:
  const prevValuesRef = useRef<SliderContentIn>();
  useEffect(() => {
    const id = window.setInterval(() => {
      if (createContentMutation.isError || updateContentMutation.isError)
        return;

      const current = form.getValues();
      if (current.status === 1) return;

      const prev = prevValuesRef.current;

      if (prev !== undefined && !isEqual(prev, current)) {
        console.log('autosave:', current);
        handleSave();
      }

      prevValuesRef.current = cloneDeep(current);
    }, AUTOSAVE_INTERVAL);

    return () => window.clearInterval(id);
  }, [form, handleSave]);

  const settings = useMemo(
    () =>
      mode === 'create'
        ? { toast: 'Content created.', toastKey: 'createContent' }
        : { toast: 'Content saved.', toastKey: 'updateContent' },
    [mode]
  );

  const onError = (er: FieldErrors<Content>) => {
    console.error({ er });

    const hasTab0Error =
      'name' in er ||
      'sites' in er ||
      'zones' in er ||
      'publication' in er ||
      'publication_urls' in er ||
      'rss_settings' in er ||
      'slides' in er;

    setSelectedTabIndex(hasTab0Error ? 0 : 1);
  };

  const onSidebarTabSelectionChange = (tabIdx: number) => {
    if (tabIdx >= 0 && tabIdx <= 1) {
      setSelectedTabIndex(tabIdx);
    }
  };

  const [selectedTabIndex, setSelectedTabIndex] = useState<number>(0);
  const [selectedPresetId, setSelectedPresetId] = useState<
    ContentStyles['id'] | undefined
  >();

  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const containerMaxWidth = `calc(100vw - ${isSidebarOpen ? '8' : '4.5'}rem)`;

  const isSaveButtonLoading =
    (mode === 'create' && createContentMutation.isLoading) ||
    (mode === 'update' && updateContentMutation.isLoading);

  const isPublishButtonLoading =
    form.getValues('status') === 0
      ? publishContentMutation.isLoading
      : unpublishContentMutation.isLoading;

  if (contentQuery.isLoading) return <PageLoader />;

  return (
    <div
      className="!max-w-screen flex h-auto min-h-full flex-row"
      style={{ maxWidth: '100vw !important' }}
    >
      <Sidebar
        mode={mode === 'create' ? mode : 'edit'}
        type="slider"
        open
        selectedTab={selectedTabIndex}
        setSelectedTab={onSidebarTabSelectionChange}
        onToggleChange={setIsSidebarOpen}
        disabled={false}
        videoConfigurationDisabled
      />

      <div
        className="flex grow flex-col bg-paper"
        style={{ maxWidth: containerMaxWidth }}
      >
        <form onSubmit={stopSubmition}>
          <div className="flex min-h-[68px] items-center gap-2 px-8 py-4 align-middle">
            <div className="flex grow text-h4 font-semibold">
              {mode === 'update' ? 'Edit' : 'Create'} slider
            </div>

            <div className="flex max-w-[50%]">
              {createContentMutation.isError && (
                <div className="text-sm font-light text-red">
                  {getErrorMessage(createContentMutation.error)}
                </div>
              )}

              {updateContentMutation.isError && (
                <div className="text-sm font-light text-red">
                  {getErrorMessage(updateContentMutation.error)}
                </div>
              )}
            </div>

            <div className="flex items-center gap-4">
              <Button
                variant="secondary"
                title="Close"
                size="sm"
                onClick={(e) => {
                  form.reset();
                  if (mode === 'update' && params.id) {
                    dispatch(
                      contentApi.util.invalidateTags([
                        { type: 'ContentList', id: params.id },
                      ])
                    );
                  }
                  props.onClose('onCloseClick', e);
                }}
              />

              <div className="flex flex-row gap-1 rounded-lg bg-white p-1">
                <Button
                  variant={
                    form.getValues('status') === 0 ? 'secondary' : 'primary'
                  }
                  title="Save"
                  size="sm"
                  className={twMerge(
                    'disabled:cursor-not-allowed',
                    form.getValues('status') === 0 ? '' : 'disabled:!bg-gray'
                  )}
                  isLoading={isSaveButtonLoading}
                  disabled={isSaveButtonLoading}
                  onClick={() => {
                    handleSave();
                  }}
                />

                <Button
                  title={
                    form.getValues('status') === 0 ? 'Publish' : 'Unpublish'
                  }
                  variant={
                    form.getValues('status') === 0 ? 'primary' : 'secondary'
                  }
                  size="sm"
                  isLoading={isPublishButtonLoading}
                  disabled={
                    isPublishButtonLoading ||
                    createContentMutation.isLoading ||
                    updateContentMutation.isLoading
                  }
                  className={twMerge(
                    'disabled:cursor-not-allowed',
                    form.getValues('status') === 0 ? 'disabled:!bg-gray' : ''
                  )}
                  onClick={async () => {
                    if (form.getValues('status') === 0) {
                      handleSave(true);
                    } else if (params.id) {
                      await unpublishContent(params.id).unwrap();
                      form.clearErrors();
                      form.setValue('status', 0);
                    }
                  }}
                />
              </div>
            </div>
          </div>

          <FormProvider {...form}>
            <div className="flex grow flex-col gap-4 px-8 pb-4">
              <div
                className={twMerge(
                  'flex grow items-start gap-4',
                  selectedTabIndex === 0 ? '' : 'hidden'
                )}
              >
                <div className="flex flex-col gap-4 rounded-lg border border-light-gray bg-white p-4 dark:border-light-gray/[.02] dark:bg-default">
                  <MetadataSettings />
                </div>
                <SlidesList />
              </div>

              <div
                className={twMerge(
                  'relative flex gap-4 rounded-lg bg-white p-4 dark:bg-default',
                  selectedTabIndex === 1 ? '' : 'hidden'
                )}
              >
                <div className="flex !w-[348px] !min-w-[348px] !max-w-[348px] flex-col gap-6">
                  <PresetSelector
                    selectedPresetId={selectedPresetId}
                    setSelectedPresetId={setSelectedPresetId}
                  />
                  <ColorsSettings />
                  <Controls />
                  <LinkButtonSettings />
                  <TextSettings />
                  <TextStyleAndPositionSettings />
                  <AnimationStyleSettings />
                  <LogoSettings />
                </div>
                <div className="flex w-full items-start justify-center overflow-hidden">
                  <SlidesPlayerWrapper />
                </div>
              </div>
            </div>
          </FormProvider>
        </form>
      </div>
    </div>
  );
}
